import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export type PreviewDevice = 'desktop' | 'tablet' | 'mobile';

export const PREVIEW_DEVICE_STORAGE_KEY = 'hostvsl:selectedPreviewDevice';

export const DEVICE_MAX_WIDTH: Record<PreviewDevice, string> = {
  desktop: '820px',
  tablet: '864px',
  mobile: '418px',
};

export function usePreviewDevice() {
  useLanguage();
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>(() => {
    if (typeof window === 'undefined') return 'desktop';
    const stored = window.sessionStorage.getItem(PREVIEW_DEVICE_STORAGE_KEY);
    return stored === 'tablet' || stored === 'mobile' ? stored : 'desktop';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(PREVIEW_DEVICE_STORAGE_KEY, previewDevice);
  }, [previewDevice]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PreviewDevice>).detail;
      if (detail && detail !== previewDevice) setPreviewDevice(detail);
    };
    window.addEventListener('hostvsl:previewDeviceChange', handler as EventListener);
    return () =>
      window.removeEventListener('hostvsl:previewDeviceChange', handler as EventListener);
  }, [previewDevice]);

  const update = (device: PreviewDevice) => {
    setPreviewDevice(device);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('hostvsl:previewDeviceChange', { detail: device }));
    }
  };

  return [previewDevice, update] as const;
}

interface DeviceFrameProps {
  device: PreviewDevice;
  children: React.ReactNode;
  className?: string;
}

export const DeviceFrame: React.FC<DeviceFrameProps> = ({ device, children, className }) => {
  useLanguage();
  if (device === 'desktop') {
    return (
      <div
        data-theme-isolated
        className={cn('w-full mx-auto', className)}
        style={{ maxWidth: '820px' }}
      >
        {children}
      </div>
    );
  }

  const isMobile = device === 'mobile';

  // Mobile: 340px ≈ chrome vertical ao redor da moldura no desktop (p-3 + header
  // 64 + padding do stage + cabeçalho 66 e rodapé 42 do card de preview + pb-8 +
  // label ≈ 328px, com folga) — mantém o iPhone inteiro na tela sem rolar o stage.
  // Tablet: 864px nominais, mas encolhe se o card for mais estreito (viewports lg
  // apertadas) em vez de ser cortada pelo overflow-hidden do card.
  const frameStyle: React.CSSProperties = isMobile
    ? {
        width: 'min(418px, calc((100vh - 340px) * 0.479))',
        aspectRatio: '418 / 872',
        padding: 14,
        borderRadius: 52,
      }
    : { width: 'min(864px, 100%)', padding: 22, borderRadius: 40 };

  const screenRadius = isMobile ? 38 : 22;

  return (
    <div className={cn('flex flex-col items-center w-full pb-8', className)}>
      <div
        className="relative mx-auto"
        style={{
          ...frameStyle,
          // Moldura em branco gelo (mobile e tablet iguais).
          background: '#E2E8F0',
          boxShadow:
            '0 30px 80px -20px rgba(15, 23, 70, 0.50), 0 8px 24px -8px rgba(15, 23, 70, 0.40), inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.10)',
        }}
      >
        {isMobile && (
          <>
            <span
              aria-hidden
              className="absolute left-[-2px] top-[110px] h-[34px] w-[3px] rounded-r-md bg-[#E2E8F0]"
            />
            <span
              aria-hidden
              className="absolute left-[-2px] top-[160px] h-[58px] w-[3px] rounded-r-md bg-[#E2E8F0]"
            />
            <span
              aria-hidden
              className="absolute left-[-2px] top-[230px] h-[58px] w-[3px] rounded-r-md bg-[#E2E8F0]"
            />
            <span
              aria-hidden
              className="absolute right-[-2px] top-[150px] h-[88px] w-[3px] rounded-l-md bg-[#E2E8F0]"
            />
          </>
        )}
        {!isMobile && (
          <>
            <span
              aria-hidden
              className="absolute right-[-2px] top-[80px] h-[60px] w-[3px] rounded-l-md bg-[#E2E8F0]"
            />
            <span
              aria-hidden
              className="absolute right-[-2px] top-[150px] h-[40px] w-[3px] rounded-l-md bg-[#E2E8F0]"
            />
          </>
        )}

        <div
          className="relative overflow-hidden bg-black"
          style={{
            borderRadius: screenRadius,
            boxShadow:
              'inset 0 0 0 1px rgba(255,255,255,0.04), inset 0 0 24px rgba(0,0,0,0.4)',
            height: isMobile ? '100%' : undefined,
          }}
        >
          {isMobile && (
            <div
              aria-hidden
              className="absolute left-1/2 -translate-x-1/2 top-2 z-10 h-[24px] w-[96px] rounded-full bg-black"
              style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.04)' }}
            />
          )}
          {!isMobile && (
            <div
              aria-hidden
              className="absolute left-1/2 -translate-x-1/2 top-[8px] z-10 h-[6px] w-[6px] rounded-full bg-[#1f2937]"
              style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.05)' }}
            />
          )}

          <div
            data-theme-isolated
            className={cn(
              'bg-white',
              // Mobile: anchored no topo (deixa espaço pra dynamic island) e
              // sem centralizar verticalmente — espelha como uma página real
              // de VSL renderiza o player no iPhone (vídeo no topo, resto em
              // branco até o fim do scroll).
              isMobile && 'hs-compact-player h-full flex items-start justify-center pt-12'
            )}
          >
            {isMobile && (
              <style>{`
                /* Escala controles e big-play apenas dentro da preview do
                   iPhone (390 × 844). Usa zoom (afeta layout) em vez de
                   transform (não afeta) pra que a barra de fundo encolha
                   junto com o conteúdo. */
                .hs-compact-player .absolute.bottom-0.left-0.w-full,
                .hs-compact-player .absolute.top-1\\/2.left-1\\/2 {
                  zoom: 0.65;
                }
                /* Esconde botão de legenda na preview do mobile (CC fica
                   apertado e pouco usado nesse contexto). */
                .hs-compact-player div:has(> button[title="Legendas"]) {
                  display: none;
                }
              `}</style>
            )}
            {children}
          </div>
        </div>
      </div>

      <span className="mt-3 text-[11px] font-medium text-[#8A99B5] tracking-wide uppercase">
        {isMobile ? translateInterfaceText("390 × 844 · iPhone") : translateInterfaceText("820 × 1180 · iPad")}
      </span>
    </div>
  );
};
